/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useContext, useEffect, useState } from 'react';
import { Title, useGetOne, useQuery } from 'react-admin';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import RouteMap from './RouteMap';
import DispatchVehicle from './DispatchVehicle';
import { DispatchContext } from '../Context';
import { areTimeSlotsEnabled, getZ3PConfigValue, pickColor } from '../helper';
import AppointmentList from './AppointmentList';
import DispatchCalendar from './DispatchCalendar';
import { CALENDAR_COLORS } from '../calendar/colors';
import DispatchVehicleTimeSlots from './DispatchVehicleTimeSlots';

const useStyles = makeStyles((theme) => ({
  vehicleId: {
    fontWeight: '700',
    width: '100%',
    marginBottom: '10px',
  },
  dispatchServiceVehicleCard: {
    userSelect: 'none',
  },
  pickerRoot: {
    '& .MuiPickersToolbar-toolbar': {
      display: 'none',
    },
    '& .MuiPickersBasePicker-pickerView': {
      minWidth: 'auto',
    },
    '& .MuiPickersBasePicker-container': {
      justifyContent: 'center',
    },
    '& .MuiPickersStaticWrapper-staticWrapperRoot': {
      minWidth: 290,
    },
    '& .MuiPickersDay-current': {
      color: theme.palette.secondary.main,
    },
    '& .MuiPickersDay-daySelected': {
      color: '#FFFFFF',
    },
  },
  viewBase: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: 10,
    display: 'block',
  },
  mapWrapper: {
    height: '100%',
    overflow: 'hidden',
  },
  mapDataContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: '600',
    color: '#848383',
  },
  cardBase: {
    '& .MuiCardContent-root': {
      padding: 5,
    },
  },
  displayList: {
    fontSize: 12,
    color: '#959393',
  },
  infoText: {
    fontSize: 11.5,
    color: '#959393',
  },
  tabSelectorWrapper: {
    display: 'block',
  },
  tabSelector: {
    width: '625px',
    borderRadius: '100px',
    border: '1px solid #707070',
    transform: 'scale(0.7)',
    display: 'flex',
    [theme.breakpoints.down(480)]: {
      width: '80vw',
      transform: 'scale(0.6)',
    },
    [theme.breakpoints.between(481, 636)]: {
      width: '70vw',
      transform: 'scale(0.5)',
      marginLeft: '-14vw',
    },
    [theme.breakpoints.between(637, 759)]: {
      transform: 'scale(0.45)',
      marginLeft: '-25vw',
    },
    [theme.breakpoints.between(760, 1000)]: {
      transform: 'scale(0.5)',
      marginLeft: '-15vw',
    },
  },
  tab: {
    borderRadius: '100px',
    maxHeight: '30px',
    minHeight: '30px',
    margin: 'auto -20px',
    '&:first-child': {
      marginLeft: '0px',
    },
    '&:last-child': {
      marginRight: '0px',
    },
    flexGrow: 1,
  },
  list: {
    width: '80%',
    listStyle: 'none',
    marginBottom: '10px',
    height: '30px',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '4px',
    paddingRight: '4px',
    fontWeight: 'bold',
  },
  selectDeselect: {
    width: '88%',
    textAlign: 'center',
  },
  selectDeselectEnabled: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  selectDeselectDisabled: {
    cursor: 'default',
    textDecoration: 'none',
    color: 'gray',
  },
}));

const SplitView = ({ loading }) => {
  const {
    schedule,
    mapData,
    setMapData,
    dispatchDate,
    handleDate,
    isEditing,
  } = useContext(DispatchContext);

  const schedulingSystem = getZ3PConfigValue('scheduling_system');
  const serviceLocations = getZ3PConfigValue('service_locations');
  // Show the map view if the Pro offers either Home or Workplace services.
  const showMapView =
    serviceLocations?.includes('home') ||
    serviceLocations?.includes('workplace');
  // Show the calendar view if Time Slots are enabled.
  const showCalendarView = areTimeSlotsEnabled();
  // Always show the list view
  const showListView = true;

  const [selectedSchedule, setSelectedSchedule] = useState(schedule?.[0]);

  let initialView = 'list';
  if (showMapView) initialView = 'map';
  if (showCalendarView) initialView = 'calendar';

  const [selectedView, setSelectedView] = useState(initialView); // Can be 'map', 'calendar', 'list'

  const [selectedServiceVehicleIds, setSelectedServiceVehicleIds] = useState(
    [],
  );
  const [serviceVehicleIdToColor, setServiceVehicleIdToColor] = useState({});

  const { data: allServiceVehicles } = useQuery({
    type: 'getList',
    resource: 'service-vehicle',
    payload: {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'id', order: 'ASC' },
      filter: { is_active: true },
    },
  });

  useEffect(() => {
    if (!selectedSchedule && schedule) {
      setSelectedSchedule(schedule?.[0]);
    } else if (
      selectedSchedule &&
      !moment(selectedSchedule?.start_time).isSame(dispatchDate, 'day')
    ) {
      setSelectedSchedule(null);
    }
  }, [schedule, dispatchDate]);

  useEffect(() => {
    if (!selectedServiceVehicleIds?.length && allServiceVehicles?.length) {
      const maxServiceVehicles = 10;
      setSelectedServiceVehicleIds(
        allServiceVehicles?.map((sv) => sv.id).slice(0, maxServiceVehicles),
      );
    }
    const serviceVehicleIds = allServiceVehicles?.map((sv) => sv.id);
    setServiceVehicleIdToColor(assignColors(serviceVehicleIds));
  }, [allServiceVehicles]);

  const assignColors = (strings) => {
    const stringToColor = serviceVehicleIdToColor;
    [...new Set(strings)].forEach((str) => {
      if (!stringToColor[str]) {
        const index =
          Object.keys(stringToColor).length % CALENDAR_COLORS.length;
        stringToColor[str] = CALENDAR_COLORS[index];
      }
    });
    return stringToColor;
  };

  const handleToggleVehicle = (serviceVehicleId, isMultiSelect) => {
    let newMapData = mapData;
    if (isMultiSelect) {
      newMapData = mapData?.map((val) =>
        val.service_vehicle_id === serviceVehicleId
          ? { ...val, selected: !val.selected }
          : { ...val },
      );
    } else {
      newMapData = mapData?.map((val) =>
        val.service_vehicle_id === serviceVehicleId
          ? { ...val, selected: true }
          : { ...val, selected: false },
      );
    }
    setMapData(newMapData);
  };

  const handleVehicleClick = (e, data) => {
    const { service_vehicle_id } = data;

    const mapDataVehicle = mapData?.find(
      (val) => val.service_vehicle_id === service_vehicle_id,
    );

    // If already selected and Shift is held, it's a deselect, so set selected schedule to be a different selected vehicle
    if (mapDataVehicle?.selected && e.shiftKey) {
      const selectedMapDatum = mapData?.find(
        (val) => val.selected && val.service_vehicle_id !== service_vehicle_id,
      );
      setSelectedSchedule(
        schedule?.find(
          (scheduleData) =>
            scheduleData.service_vehicle_id ===
            selectedMapDatum?.service_vehicle_id,
        ),
      );
    } else {
      setSelectedSchedule(data);
    }
    handleToggleVehicle(service_vehicle_id, e.shiftKey);
  };

  const toggleServiceVehicleId = (serviceVehicleId) => {
    if (selectedServiceVehicleIds.includes(serviceVehicleId)) {
      setSelectedServiceVehicleIds(
        selectedServiceVehicleIds.filter((sv) => sv !== serviceVehicleId),
      );
    } else {
      setSelectedServiceVehicleIds([
        ...selectedServiceVehicleIds,
        serviceVehicleId,
      ]);
    }
  };

  const renderTabSelector = () => {
    return (
      <div className={classes.tabSelectorWrapper}>
        <div className={classes.tabSelector}>
          {showMapView && (
            <Button
              className={classes.tab}
              variant="contained"
              color={selectedView === 'map' ? 'primary' : 'default'}
              onClick={() => setSelectedView('map')}
              style={{ zIndex: selectedView === 'map' ? 3 : 1 }}
            >
              Map
            </Button>
          )}
          {showCalendarView && (
            <Button
              className={classes.tab}
              variant="contained"
              color={selectedView === 'calendar' ? 'primary' : 'default'}
              onClick={() => setSelectedView('calendar')}
              style={{ zIndex: selectedView === 'calendar' ? 3 : 2 }}
            >
              Calendar
            </Button>
          )}
          {showListView && (
            <Button
              className={classes.tab}
              variant="contained"
              color={selectedView === 'list' ? 'primary' : 'default'}
              onClick={() => setSelectedView('list')}
              style={{ zIndex: selectedView === 'list' ? 3 : 1 }}
            >
              List
            </Button>
          )}
        </div>

        {selectedView !== 'calendar' && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={1.55}
            mb={1}
            style={{
              width: '625px',
            }}
          >
            <Typography variant="h4">
              {moment(dispatchDate).format('dddd, MMMM DD')}
            </Typography>
          </Box>
        )}
      </div>
    );
  };

  const sortServiceVehiclesByName = (serviceVehicles) => {
    return (
      serviceVehicles &&
      serviceVehicles.sort(function(a, b) {
        const x = a.name;
        const y = b.name;
        return x.localeCompare(y);
      })
    );
  };

  const classes = useStyles();

  const handleSelectAll = () => {
    if (allServiceVehicles?.length) {
      setSelectedServiceVehicleIds(allServiceVehicles.map((sv) => sv.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedServiceVehicleIds([]);
  };

  const allSelected =
    selectedServiceVehicleIds?.length === allServiceVehicles?.length;
  const noneSelected = selectedServiceVehicleIds?.length === 0;

  const selectAllOrNoneButtons = (
    <p className={classes.selectDeselect}>
      Select :{' '}
      <span
        onClick={handleSelectAll}
        onKeyDown={handleSelectAll}
        className={
          allSelected
            ? classes.selectDeselectDisabled
            : classes.selectDeselectEnabled
        }
      >
        All
      </span>{' '}
      |{' '}
      <span
        onClick={handleDeselectAll}
        onKeyDown={handleDeselectAll}
        className={
          noneSelected
            ? classes.selectDeselectDisabled
            : classes.selectDeselectEnabled
        }
      >
        None
      </span>
    </p>
  );

  return (
    <Box>
      <Title title="Dispatch" />
      <Grid container>
        <Grid item xs={3}>
          <Box className={classes.viewBase}>
            <Box>
              <Box className={classes.pickerRoot}>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <DatePicker
                    autoOk
                    orientation="landscape"
                    variant="static"
                    openTo="date"
                    value={dispatchDate}
                    format="MMMM DD, YYYY"
                    onChange={handleDate}
                  />
                </MuiPickersUtilsProvider>
              </Box>
            </Box>
            {selectedView === 'map' ? (
              <Box>
                {schedule?.length === 0 && 'No vehicles.'}
                {schedule?.map((data) => (
                  <Card
                    onClick={(e) => !isEditing && handleVehicleClick(e, data)}
                    className={classes.cardBase}
                    style={{
                      cursor: isEditing ? 'no-drop' : 'pointer',
                      padding: '10px 0px 0px 5px',
                      margin: '10px 10px 20px 10px',
                      // eslint-disable-next-line no-nested-ternary
                      border: (
                        mapData?.find(
                          (datum) =>
                            datum.service_vehicle_id ===
                              data.service_vehicle_id &&
                            datum.selected &&
                            datum.stops.length > 0,
                        ) || {}
                      ).service_vehicle_id
                        ? `3px solid ${pickColor(data.service_vehicle_id)}`
                        : (
                            mapData?.find(
                              (datum) =>
                                datum.service_vehicle_id ===
                                  data.service_vehicle_id &&
                                datum.selected &&
                                datum.stops.length === 0,
                            ) || {}
                          ).service_vehicle_id
                        ? `3px solid rgb(226 223 223)`
                        : `1px solid rgb(226 223 223)`,
                    }}
                  >
                    <CardContent>
                      <VehicleCardContent data={data} />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <>
                <ul>
                  {allServiceVehicles?.length > 1 && selectAllOrNoneButtons}

                  {sortServiceVehiclesByName(allServiceVehicles)?.map(
                    (serviceVehicle) => (
                      <li
                        onClick={() =>
                          toggleServiceVehicleId(serviceVehicle.id)
                        }
                        className={classes.list}
                        style={{
                          border: `1px solid ${serviceVehicleIdToColor[
                            serviceVehicle.id
                          ] || '#DDDDDD'}`,
                          borderLeft: `10px solid ${serviceVehicleIdToColor[
                            serviceVehicle.id
                          ] || '#DDDDDD'}`,
                        }}
                      >
                        {serviceVehicle.name}
                        <Checkbox
                          checked={selectedServiceVehicleIds.includes(
                            serviceVehicle.id,
                          )}
                        />
                      </li>
                    ),
                  )}
                </ul>
              </>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={9}
          className={classes.fullHeight}
          style={{ overflow: 'hidden' }}
        >
          {renderTabSelector()}
          {loading && 'Loading...'}
          {!loading && selectedView === 'map' && (
            <div style={{ display: 'flex', height: '100%' }}>
              <Grid item xs={4} className={classes.viewBase}>
                {schedulingSystem === 'time_slots' ? (
                  <DispatchVehicleTimeSlots
                    data={selectedSchedule}
                    date={dispatchDate}
                  />
                ) : (
                  <DispatchVehicle data={selectedSchedule} />
                )}
              </Grid>
              <Grid item xs={8} className={classes.mapWrapper}>
                <RouteMap date={dispatchDate} />
              </Grid>
            </div>
          )}
          {!loading && selectedView === 'calendar' && (
            <>
              <Grid item xs={12} className={classes.viewBase}>
                <DispatchCalendar
                  date={dispatchDate}
                  selectedServiceVehicleIds={selectedServiceVehicleIds}
                  serviceVehicleIdToColor={serviceVehicleIdToColor}
                />
              </Grid>
            </>
          )}
          {!loading && selectedView === 'list' && (
            <Grid item xs={12} className={classes.viewBase}>
              <AppointmentList
                resource="scheduled-work"
                basePath="scheduled-work"
                selectedDate={moment(dispatchDate).format('YYYY-MM-DD')}
                selectedServiceVehicleIds={selectedServiceVehicleIds}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SplitView;

const VehicleCardContent = ({ data }) => {
  const { service_vehicle_id } = data;
  const { data: vehicleDetail } = useGetOne(
    'service-vehicle',
    service_vehicle_id,
  );

  const classes = useStyles();
  const { name } = vehicleDetail || {};

  return (
    <div className={classes.dispatchServiceVehicleCard}>
      <div className={classes.vehicleId}>{name}</div>
      <Divider className={classes.listDivider} />
      <ApptsDetails service_vehicle_id={service_vehicle_id} />
    </div>
  );
};

const ApptsDetails = ({ service_vehicle_id }) => {
  const { schedule } = useContext(DispatchContext);
  const classes = useStyles();
  if (!schedule) {
    return null;
  }
  const {
    current_scheduled_works = 0,
    current_total_hours = 0,
    limit_total_hours = 0,
  } =
    schedule.find((datum) => datum.service_vehicle_id === service_vehicle_id) ||
    {};
  return (
    <Box key={current_total_hours}>
      <Box className={classes.mapDataContainer}>
        <Box mt={1.3} mb={0.5}>
          <Typography variant="body2" className={classes.infoText}>
            Appointments: {current_scheduled_works}{' '}
          </Typography>
        </Box>
        <Box mt={1.3} mb={0.5}>
          <Typography variant="body2" className={classes.infoText}>
            Total Hours Booked: {`${current_total_hours}/${limit_total_hours}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
